import { ReactNode } from "react";

import {
  FormattedDate,
  FormattedMessage,
  defineMessages,
  useIntl,
} from "react-intl";
import { Link, makeStyles, tokens } from "@fluentui/react-components";

import type { TalentDetails } from "@aglocal/schema/Interview";

import Tag, { TagGroup } from "@aglocal/web/components/Tag";
import { Grid, Row, Col } from "@aglocal/web/components/Grid";
import { Span } from "@aglocal/web/components/Typography";

const messages = defineMessages({
  jobTitle: {
    defaultMessage: "Job Title",
    description: "Row label on the talent's Connected profile summary.",
  },
  location: {
    defaultMessage: "Location",
    description: "Row label on the talent's Connected profile summary.",
  },
  resumeReceived: {
    defaultMessage: "Resume Received",
    description:
      "Row label on the talent's Connected profile summary, followed by the date of the latest resume.",
  },
  previousNotes: {
    defaultMessage: "Previous G2 Notes",
    description:
      "Row label on the talent's Connected profile summary; G2 is an Allegis term for a get-to-know-you meeting.",
  },
  skills: {
    defaultMessage: "Skills",
    description: "Row label on the talent's Connected profile summary.",
  },
  connected: {
    defaultMessage: "Connected",
    description:
      "Row label on the talent's Connected profile summary, followed by a link to the profile. Connected is the CRM's name.",
  },
  unknown: {
    defaultMessage: "Unknown",
    description:
      "Shown on the talent's Connected profile summary when a value is missing.",
  },
  noResume: {
    defaultMessage: "No resume on file",
    description:
      "Shown on the talent's Connected profile summary when there is no resume.",
  },
  noNotes: {
    defaultMessage: "No previous meeting notes",
    description:
      "Shown on the talent's Connected profile summary when there are no notes from earlier meetings.",
  },
  noSkills: {
    defaultMessage: "No skills on file",
    description:
      "Shown on the talent's Connected profile summary when the profile lists no skills.",
  },
  viewInConnected: {
    defaultMessage: "View In Connected",
    description: "Link that opens the talent's profile in Connected.",
  },
});

const useStyles = makeStyles({
  skill: {
    backgroundColor: tokens.colorPaletteBlueBackground2,
  },
});

const useDetailStyles = makeStyles({
  root: {
    marginLeft: 0,
    marginBottom: tokens.spacingVerticalS,
  },
  title: {
    fontWeight: tokens.fontWeightBold,
  },
  value: {
    marginLeft: 0,
  },
});

function formatLocation({ mailingCity, mailingCountry }: TalentDetails) {
  const parts = [mailingCity, mailingCountry]
    .map((value) => value?.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
}

function getKnownJobTitle({ jobTitle }: TalentDetails): string | null {
  const title = jobTitle?.trim();
  return title && title.toLowerCase() !== "unknown" ? title : null;
}

interface DetailProps {
  title: string;
  children: ReactNode;
}

function Detail({ title, children }: DetailProps) {
  const styles = useDetailStyles();
  return (
    <Row as="dl" className={styles.root}>
      <Col as="dt" sm="3" className={styles.title}>
        {title}
      </Col>
      <Col as="dd" sm="9" className={styles.value}>
        {children}
      </Col>
    </Row>
  );
}

export interface ConnectedDetailsProps {
  details: TalentDetails;
}

export default function ConnectedDetails({ details }: ConnectedDetailsProps) {
  const { $t } = useIntl();
  const styles = useStyles();
  const unknown = (
    <Span italic>
      <FormattedMessage {...messages.unknown} />
    </Span>
  );
  return (
    <Grid>
      <Detail title={$t(messages.jobTitle)}>
        {getKnownJobTitle(details) ?? unknown}
      </Detail>
      <Detail title={$t(messages.location)}>
        {formatLocation(details) ?? unknown}
      </Detail>
      <Detail title={$t(messages.resumeReceived)}>
        {details.latestResumeDate ? (
          <FormattedDate value={details.latestResumeDate} />
        ) : (
          <Span italic>
            <FormattedMessage {...messages.noResume} />
          </Span>
        )}
      </Detail>
      <Detail title={$t(messages.previousNotes)}>
        {details.g2Notes ? (
          <Span whiteSpace="break-spaces">{details.g2Notes}</Span>
        ) : (
          <Span italic>
            <FormattedMessage {...messages.noNotes} />
          </Span>
        )}
      </Detail>
      <Detail title={$t(messages.skills)}>
        {details.skills && details.skills.length > 0 ? (
          <TagGroup>
            {details.skills.map((skill, index) => (
              <Tag key={index} className={styles.skill}>
                {skill}
              </Tag>
            ))}
          </TagGroup>
        ) : (
          <Span italic>
            <FormattedMessage {...messages.noSkills} />
          </Span>
        )}
      </Detail>
      <Detail title={$t(messages.connected)}>
        <Link
          as="a"
          href={`https://allegisgroup.lightning.force.com/lightning/r/Contact/${details.contactId}/view`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FormattedMessage {...messages.viewInConnected} />
        </Link>
      </Detail>
    </Grid>
  );
}
